import { useMemo } from 'react';
import { useOrganizationId } from '@features/user';
import type { DefinitionResponse } from '@store/generated/patient-requirement';

import {
  CREATABLE_KINDS,
  type RequirementKind,
  SINGLETON_KINDS,
} from '../constants';

import { useGetRequirementDefinitionQuery, useGetRequirementDefinitionsQuery } from './api';

/** How often the create confirmation re-reads a definition that is still checking. */
const MATERIALIZATION_POLL_MS = 2000;

export interface RequirementCatalog {
  organizationId: string | null | undefined;
  definitions: DefinitionResponse[];
  /** First load only. The view may hide the table for this. */
  isLoading: boolean;
  /** A background revalidation over data already on screen. Never hides rows. */
  isRefreshing: boolean;
  isError: boolean;
  refetch: () => void;
  /**
   * Kinds staff may still create. A singleton kind drops out once the org holds
   * one (AC-1) — including an INACTIVE one, because the backend's uniqueness is
   * per org and kind, not per active definition, so offering it again would
   * produce a 409 the staff member cannot act on.
   */
  availableKinds: RequirementKind[];
}

/**
 * The org's requirement catalog. Waits for the organization id rather than
 * firing a request against `undefined`.
 */
export const useRequirementCatalog = ({
  skip: skipRequested = false,
}: { skip?: boolean } = {}): RequirementCatalog => {
  const organizationId = useOrganizationId();

  const skip = skipRequested || !organizationId;

  const { data, isLoading, isFetching, isError, refetch } =
    useGetRequirementDefinitionsQuery({ orgId: organizationId ?? '' }, { skip });

  // Every label save and every toggle invalidates the list tag, so isFetching
  // goes true on the golden path. The view treats its loading flag as "replace
  // the table with a spinner", which would unmount every row mid-edit and throw
  // away the per-row autosave status the admin is watching. Only a first load
  // (no data yet) may hide the table; later revalidations stay invisible.
  const hasLoadedOnce = data !== undefined;

  const definitions = useMemo(() => data?.items ?? [], [data]);

  const availableKinds = useMemo(() => {
    const takenSingletons = new Set(
      definitions
        .map(definition => definition.kind)
        .filter(kind => (SINGLETON_KINDS as readonly string[]).includes(kind)),
    );
    return CREATABLE_KINDS.filter(kind => !takenSingletons.has(kind));
  }, [definitions]);

  return {
    organizationId,
    definitions,
    // Keep the skeleton up while the org id resolves; otherwise the section
    // flashes an empty catalog before the request it is waiting on can start.
    // A skipped catalog is not loading — it is deliberately idle.
    isLoading: !skipRequested && (!organizationId || (!hasLoadedOnce && (isLoading || isFetching))),
    isRefreshing: hasLoadedOnce && isFetching,
    isError,
    // RTK Query's refetch returns a promise; the catalog's retry button wants
    // a void handler, so swallow it here rather than at each call site.
    refetch: () => {
      void refetch();
    },
    availableKinds,
  };
};

/**
 * Poll one definition while its panel evaluation runs (AC-21). Returns the live
 * definition so the create confirmation can show "checking existing records"
 * and then the counts, with no staff re-trigger.
 *
 * Polling stops as soon as the definition reports `done`.
 */
export const useDefinitionMaterialization = (
  definitionId: string | undefined,
): {
  definition: DefinitionResponse | undefined;
  isChecking: boolean;
} => {
  const organizationId = useOrganizationId();
  const skip = !organizationId || !definitionId;

  // Read the cached value first so the interval is chosen from the CURRENT
  // status: passing a fixed interval would keep polling a finished definition.
  const { data } = useGetRequirementDefinitionQuery(
    { orgId: organizationId ?? '', definitionId: definitionId ?? '' },
    { skip },
  );

  const isChecking = data?.materialization_status === 'checking';

  useGetRequirementDefinitionQuery(
    { orgId: organizationId ?? '', definitionId: definitionId ?? '' },
    {
      skip: skip || !isChecking,
      pollingInterval: MATERIALIZATION_POLL_MS,
    },
  );

  return {
    definition: data,
    // Treat "not loaded yet" as still checking so the confirmation never shows
    // a zeroed count row before the first response lands.
    isChecking: !skip && (data === undefined || isChecking),
  };
};
